import { Search, X } from 'lucide-react';
import type { DateRange } from 'react-day-picker';

import { cn } from '@/lib/shadcn/utils';
import { Button } from '@/modules/ui/components/button/Button';
import { Combobox } from '@/modules/ui/components/combobox/Combobox';
import { DateRangePicker } from '@/modules/ui/components/date-range-picker/DateRangePicker';

import { type OutreachStatus, outreachStatuses } from './outreachThread';

export const ALL_JOBS = 'all';

interface MessagesFilterBarProps {
  status: OutreachStatus | undefined;
  jobId: string;
  jobs: { id: string; title: string }[];
  range: DateRange | undefined;
  search: string;
  /** Owned by the page, so the empty state can offer the same reset. */
  hasFilters: boolean;
  onClearFilters: () => void;
  onStatusChange: (status: OutreachStatus | undefined) => void;
  onJobChange: (jobId: string) => void;
  onRangeChange: (range: DateRange | undefined) => void;
  onSearchChange: (search: string) => void;
}

/**
 * Every filter here is applied by the API, so each change starts a fresh
 * page sequence rather than narrowing what is already loaded.
 */
export function MessagesFilterBar({
  status,
  jobId,
  jobs,
  range,
  search,
  hasFilters,
  onClearFilters,
  onStatusChange,
  onJobChange,
  onRangeChange,
  onSearchChange,
}: MessagesFilterBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <div aria-label="Status" className="flex items-center gap-1" role="group">
        <StatusButton
          isCurrent={status === undefined}
          label="All"
          onClick={() => onStatusChange(undefined)}
        />
        {outreachStatuses.map((value) => (
          <StatusButton
            isCurrent={status === value}
            key={value}
            label={value === 'Pending' ? 'Queued' : value}
            onClick={() => onStatusChange(value)}
          />
        ))}
      </div>

      <span aria-hidden className="bg-border mx-1 h-4 w-px" />

      <Combobox
        className="h-7 w-[180px] border-0 px-2 text-[13px] font-medium shadow-none"
        disableSort
        emptyMessage="No jobs found."
        onChange={onJobChange}
        options={[
          { value: ALL_JOBS, label: 'All jobs' },
          ...jobs
            .map((job) => ({ value: job.id, label: job.title }))
            .sort((a, b) =>
              a.label.localeCompare(b.label, undefined, { numeric: true }),
            ),
        ]}
        searchPlaceholder="Search jobs"
        value={jobId}
      />

      <DateRangePicker
        className="h-7 border-0 px-2 text-[13px] font-medium shadow-none"
        onChange={onRangeChange}
        placeholder="Any date"
        value={range}
      />

      {hasFilters && (
        <Button
          className="h-7 rounded-md px-2 text-[13px]"
          onClick={onClearFilters}
          size="sm"
          variant="ghost"
        >
          <X className="size-3.5" />
          Clear
        </Button>
      )}

      <label className="border-input flex h-7 w-full items-center gap-1.5 rounded-md border px-2 text-[13px] text-neutral-500 sm:ml-auto sm:w-auto">
        <Search aria-hidden className="size-3.5" />
        <input
          aria-label="Search by name"
          autoFocus
          className="placeholder:text-input-placeholder w-full bg-transparent outline-none sm:w-36"
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search by name"
          type="search"
          value={search}
        />
      </label>
    </div>
  );
}

function StatusButton({
  isCurrent,
  label,
  onClick,
}: {
  isCurrent: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      aria-pressed={isCurrent}
      className={cn(
        'focus-visible:ring-ring/50 inline-flex h-7 cursor-pointer items-center rounded-md px-2 text-[13px] font-medium transition-colors duration-100 outline-none focus-visible:ring-[3px]',
        isCurrent
          ? 'bg-muted text-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
      onClick={onClick}
      type="button"
    >
      {label}
    </button>
  );
}
